/* eslint-disable no-param-reassign */
import WebViewer, { Core, WebViewerInstance } from '@pdftron/webviewer';
import { parse, stringify } from 'query-string';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { AttachmentsAPI, FileAttachment } from 'api/attachments';
import { Discussion as IDiscussion } from 'api/document/types/document';
import { useUser } from 'hooks/use-user';
import {
  useAddCommentOnAttachment,
  useGetDiscussionsForAttachment,
} from 'queries/attachments';
import { extname } from 'utils/files';
import { fullName } from 'utils/full-name';
import {
  ANNOTATION_ARROW_MODE,
  ANNOTATION_EDIT_MODE,
  ANNOTATION_RECT_MODE,
  ANNOTATION_SQUIGGLY_MODE,
  ANNOTATION_STICKY_MODE,
  decreaseAnnotationOpacity,
  disableRotation,
  FLOATING_FORM_SPACING,
  getRelativeXForAnnotation,
  getRelativeYForAnnotation,
  increaseAnnotationOpacity,
  mergeAllComments,
  parser,
  toXfdf,
} from './constants';
import './image-preview.css';
import { AnnotationChangeActions, AnnotationSelectActions } from './types';
import { getWebviewerConfig } from './webviewer-config';

interface UseImagePreviewProps {
  data?: FileAttachment;
  attachmentId?: number;
  src?: string;
  activeToolOverlay?: string;
  setSelectedInstance?: (instance: WebViewerInstance | null) => void;
  setDiscussionAttachmentId?: (value: number) => void;
  setActiveToolOverlay?: (activeTool: string) => void;
  setChatSectionVisiblity?: (value: boolean) => void;
  isRightAttachment?: boolean;
  previewConfig?: {
    zoomTogether?: boolean;
    rotateTogether?: boolean;
    scrollTogether?: boolean;
    isOverlay?: boolean;
  };
  annotationVisiblity?: boolean;
  showResolvedComments: boolean;
  setShowResolvedComments: (value: boolean) => void;
}

export const useImagePreview = ({
  data,
  attachmentId,
  src,
  activeToolOverlay,
  setSelectedInstance,
  setDiscussionAttachmentId,
  setActiveToolOverlay,
  isRightAttachment,
  setChatSectionVisiblity,
  previewConfig,
  annotationVisiblity,
  showResolvedComments,
  setShowResolvedComments,
}: UseImagePreviewProps) => {
  const { user } = useUser();
  // eslint-disable-next-line prefer-const
  let instance = useRef<WebViewerInstance | null>(null);
  const [activeAnnotation, setActiveAnnotation] =
    useState<Core.Annotations.Annotation | null>(null);
  const [floatingFormCoordinates, setFloatingFormCoordinates] = useState({
    x: 0,
    y: 0,
  });
  const ref = useRef<HTMLDivElement | null>(null);
  const params = useLocation();
  const history = useHistory();
  const [rotate, setRotate] = useState(0);
  const [isHover, setIsHover] = useState(false);
  const [, reRender] = useState<any>(null);
  const [activeDiscussion, setActiveDiscussion] =
    useState<IDiscussion | null>(null);
  const [activeTool, setActiveTool] = useState(
    activeToolOverlay || ANNOTATION_STICKY_MODE,
  );
  const [addNewCommentFormVisiblity, setAddNewCommentFormVisiblity] =
    useState(false);
  const [zoom, setZoom] = useState(1);
  const [isPrivate, setPrivate] = useState(false);
  const scrollLeft = useRef(0);
  const scrollTop = useRef(0);
  const { data: discussionsInAttachment, refetch } =
    useGetDiscussionsForAttachment(Number(attachmentId));
  const { mutateAsync: addComment } = useAddCommentOnAttachment(
    Number(attachmentId),
  );
  const { search } = useLocation();
  const values = parse(search);

  const filteredDicsussions = useMemo(() => {
    const preFilteredDiscussions = discussionsInAttachment?.discussions ?? [];

    return preFilteredDiscussions.filter((e) => {
      if (e.comments[0]?.private === true) {
        if (e.comments[0]?.user.userType === user.userType) {
          return true;
        }

        return false;
      }

      return true;
    });
  }, [discussionsInAttachment?.discussions, user.userType]);

  const discussions = useMemo(
    () =>
      filteredDicsussions.filter((e) => {
        if (e.commentThread.resolved && !showResolvedComments) {
          return false;
        }

        return true;
      }),
    [filteredDicsussions, showResolvedComments],
  );

  const resolvedDiscussions = useMemo(
    () =>
      filteredDicsussions.filter((e) => {
        if (e.commentThread.resolved) {
          return true;
        }

        return false;
      }),
    [filteredDicsussions],
  );

  const getDiscussionFoAnnotation = useCallback(
    (annotation: Core.Annotations.Annotation) => {
      return filteredDicsussions.find((discussion) =>
        discussion.comments.find(
          (comment) => comment.commentGuid === annotation.Id,
        ),
      );
    },
    [filteredDicsussions],
  );

  const onSubmit = useCallback(
    (content: string) => {
      if (activeAnnotation && data && instance.current) {
        instance.current?.Core.annotationManager
          .exportAnnotations({ annotList: [activeAnnotation] })
          .then(async (xfdf) => {
            addComment({
              content,
              commentGuid: activeAnnotation.Id,
              commentMeta: { annotations: parser.parse(xfdf) },
              isPrivate,
            }).then(() => {
              setPrivate(false);
              instance.current?.UI.setToolMode(ANNOTATION_EDIT_MODE);
            });
          });
        setAddNewCommentFormVisiblity(false);
      }
    },
    [activeAnnotation, addComment, data, isPrivate],
  );

  const setupToolsForAnnotations = useCallback(
    (_instance: WebViewerInstance) => {
      if (_instance) {
        _instance.UI.setToolMode(ANNOTATION_STICKY_MODE);
        const { Annotations } = _instance.Core;
        const { ControlHandle, SelectionModel } = Annotations;

        ControlHandle.color = new Annotations.Color(255, 255, 255, 1);
        ControlHandle.outlineColor = new Annotations.Color(15, 98, 254, 1);
        ControlHandle.handleHeight = 6;
        ControlHandle.handleWidth = 6;
        ControlHandle.shadowColor = new Annotations.Color(255, 255, 255, 0);
        SelectionModel.useDashedLine = false;
        ControlHandle.rotationHandleHeight = 0;
        ControlHandle.rotationHandleWidth = 0;
        SelectionModel.defaultSelectionOutlineColor = new Annotations.Color(
          15,
          98,
          254,
          1,
        );
        SelectionModel.selectionOutlineThickness = 1;
        SelectionModel.selectionOutlineDashSize = 0;

        const stickyTool: Core.Tools.StickyCreateTool =
          _instance.Core.documentViewer.getTool(
            ANNOTATION_STICKY_MODE,
          ) as Core.Tools.StickyCreateTool;

        stickyTool.setStyles({ BorderRadius: '0' });
        const arrowTool = _instance.Core.documentViewer.getTool(
          ANNOTATION_ARROW_MODE,
        ) as Core.Tools.ArrowCreateTool;

        const rectTool =
          _instance.Core.documentViewer.getTool(ANNOTATION_RECT_MODE);

        const squigglyTool = _instance.Core.documentViewer.getTool(
          ANNOTATION_SQUIGGLY_MODE,
        );

        [arrowTool, rectTool, squigglyTool].forEach((tool) =>
          tool.setStyles(() => ({ StrokeThickness: 4, Opacity: 1 })),
        );

        _instance.UI.iframeWindow.document
          .querySelector('.DocumentContainer')
          ?.addEventListener('mousedown', (event: any) => {
            if (event.currentTarget === event.target) {
              _instance?.UI.setToolMode(ANNOTATION_EDIT_MODE);
              setActiveAnnotation(null);
              setActiveDiscussion(null);
              setAddNewCommentFormVisiblity(false);
              _instance?.Core.annotationManager.deselectAllAnnotations();
            }
          });
      }
    },
    [setActiveAnnotation, setActiveDiscussion, setAddNewCommentFormVisiblity],
  );

  const updateAnnotationVisiblity = useCallback(
    (annVisiblity: boolean, ann: Core.Annotations.Annotation) => {
      if (instance.current) {
        const { annotationManager } = instance.current.Core;

        // eslint-disable-next-line no-param-reassign
        ann.Hidden = annVisiblity;
        annotationManager.updateAnnotation(ann);
      }
    },
    [],
  );

  const importAnnotations = useCallback(
    (newInstance: WebViewerInstance) => {
      if (filteredDicsussions) {
        const allComments = mergeAllComments(filteredDicsussions);

        newInstance.Core.documentViewer?.setDocumentXFDFRetriever(
          async () => allComments,
        );
      }
    },
    [filteredDicsussions],
  );

  const beforeAddingAnnotation = useCallback(
    (_annotations: Core.Annotations.Annotation[]) => {
      instance.current?.Core.annotationManager
        .getAnnotationsList()
        .forEach(decreaseAnnotationOpacity);
      _annotations.forEach((ann) => {
        increaseAnnotationOpacity(ann);
        disableRotation(ann);
      });
      _annotations.forEach((ann) => {
        if (ann.ToolName.includes(ANNOTATION_STICKY_MODE)) {
          const discussion = filteredDicsussions?.find((dis) =>
            dis.comments.find((c) => c.commentGuid === ann.Id),
          );

          if (!discussion) {
            ann.setY(getRelativeYForAnnotation(ann, zoom));
            ann.setX(getRelativeXForAnnotation(ann, zoom));
          }
        }
      });
    },
    [filteredDicsussions, instance, zoom],
  );

  const onClickInCanvas = useCallback(
    (event: any) => {
      if (instance.current && data) {
        const { x, y } = floatingFormCoordinates;
        const iframeRect =
          instance.current.UI.iframeWindow.document.documentElement.getBoundingClientRect();
        const [selectedAnnotation] =
          instance.current.Core.annotationManager.getSelectedAnnotations();

        if (setDiscussionAttachmentId) {
          setDiscussionAttachmentId(data.attachmentId);
        }

        if (
          x !== event.clientX + iframeRect.left + FLOATING_FORM_SPACING &&
          y !== event.clientY + iframeRect.top + FLOATING_FORM_SPACING &&
          selectedAnnotation !== activeAnnotation
        ) {
          setFloatingFormCoordinates(
            isRightAttachment
              ? {
                  x:
                    event.clientX +
                    iframeRect.width +
                    iframeRect.left +
                    FLOATING_FORM_SPACING,
                  y: event.clientY + iframeRect.top + FLOATING_FORM_SPACING,
                }
              : {
                  x: event.clientX + iframeRect.left + FLOATING_FORM_SPACING,
                  y: event.clientY + iframeRect.top + FLOATING_FORM_SPACING,
                },
          );
        }
      }
    },
    [
      instance,
      floatingFormCoordinates,
      setDiscussionAttachmentId,
      activeAnnotation,
      data,
      setFloatingFormCoordinates,
      isRightAttachment,
    ],
  );

  const onHoverInCanvas = useCallback(() => {
    setIsHover(true);
    if (instance.current && setSelectedInstance) {
      setSelectedInstance(instance.current);
    }
  }, [setSelectedInstance]);

  const onScrollInCanvas = useCallback(() => {
    if (instance.current && previewConfig?.scrollTogether && isHover) {
      const documentContainer =
        instance?.current.UI.iframeWindow.document?.querySelector(
          '.DocumentContainer',
        );

      scrollLeft.current = documentContainer?.scrollLeft ?? 0;
      scrollTop.current = documentContainer?.scrollTop ?? 0;

      values.scrollTop = documentContainer?.scrollTop.toString() ?? '';
      values.scrollLeft = documentContainer?.scrollLeft.toString() ?? '';

      const searchParamsList = stringify(values);

      history.replace({
        search: searchParamsList,
      });
    }
  }, [history, isHover, previewConfig?.scrollTogether, values]);

  const onNotHoverInCanvas = useCallback(() => {
    setIsHover(false);
  }, []);

  const addEventListenerToFindCoordinates = useCallback(() => {
    if (instance.current) {
      instance.current.UI.iframeWindow.document
        ?.querySelector('.DocumentContainer')
        ?.addEventListener('mouseup', onClickInCanvas);
      instance.current.UI.iframeWindow.document
        ?.querySelector('.DocumentContainer')
        ?.addEventListener('mouseover', onHoverInCanvas);
      instance.current.UI.iframeWindow.document
        ?.querySelector('.DocumentContainer')
        ?.addEventListener('mouseout', onNotHoverInCanvas);
      instance.current.UI.iframeWindow.document
        ?.querySelector('.DocumentContainer')
        ?.addEventListener('scroll', onScrollInCanvas);
      instance.current.UI.iframeWindow.document.addEventListener(
        'keydown',
        (e) => {
          if (e.key !== 'Backspace') {
            e.stopImmediatePropagation();
            e.stopPropagation();
          }
        },
      );
    }

    return () => {
      instance.current?.UI.iframeWindow?.document
        ?.querySelector('.DocumentContainer')
        ?.removeEventListener('mouseup', onClickInCanvas);
      instance.current?.UI.iframeWindow.document
        ?.querySelector('.DocumentContainer')
        ?.removeEventListener('mouseover', onHoverInCanvas);
      instance.current?.UI.iframeWindow.document
        ?.querySelector('.DocumentContainer')
        ?.removeEventListener('mouseout', onNotHoverInCanvas);
      instance.current?.UI.iframeWindow.document
        ?.querySelector('.DocumentContainer')
        ?.removeEventListener('scroll', onScrollInCanvas);
    };
  }, [
    instance,
    onClickInCanvas,
    onHoverInCanvas,
    onScrollInCanvas,
    onNotHoverInCanvas,
  ]);

  const onAnnotationsLoaded = useCallback(
    (_annotations: Core.Annotations.Annotation[]) => {
      const annotationList =
        instance.current?.Core.annotationManager.getAnnotationsList();

      if (annotationList) {
        const guidList = resolvedDiscussions
          .map((e) => e.comments)
          .flatMap((e) => e)
          .map((comment) => comment.commentGuid);

        const resolvedAnnotationList = annotationList.filter((ann) =>
          guidList.includes(ann.Id),
        );

        resolvedAnnotationList.forEach((ann) =>
          updateAnnotationVisiblity(!showResolvedComments, ann),
        );
      }
      addEventListenerToFindCoordinates();
      instance.current?.Core.annotationManager.deselectAllAnnotations();
    },
    [
      addEventListenerToFindCoordinates,
      resolvedDiscussions,
      showResolvedComments,
      updateAnnotationVisiblity,
      instance,
    ],
  );

  const onAnnotationsChange = useCallback(
    (
      _annotations: Core.Annotations.Annotation[],
      actions: AnnotationChangeActions,
    ) => {
      const [newAnnotation] = _annotations;

      switch (actions) {
        case 'add':
          instance.current?.Core.annotationManager.selectAnnotation(
            _annotations[0],
          );
          beforeAddingAnnotation(_annotations);
          if (_annotations.length === 1) {
            setAddNewCommentFormVisiblity(true);
            setActiveAnnotation(newAnnotation);
          }

          return;

        case 'modify':
          _annotations.forEach((annotation) => {
            const commentForThisAnnotation = filteredDicsussions
              ?.find(({ comments }) =>
                comments.find(
                  (comment) => comment.commentGuid === annotation.Id,
                ),
              )
              ?.comments.find(
                (comment) => comment.commentGuid === annotation.Id,
              );

            if (commentForThisAnnotation && data && instance.current) {
              toXfdf(instance.current, annotation).then((exportedAnnotation) =>
                AttachmentsAPI.updateComment(
                  data.attachmentId,
                  commentForThisAnnotation.commentId,
                  {
                    commentMeta: {
                      ...commentForThisAnnotation.commentMeta,
                      annotations: parser.parse(exportedAnnotation),
                    },
                  },
                ),
              );
            }
          });
          break;

        case 'delete':
          _annotations.forEach((annotation) => {
            const commentForThisAnnotation = filteredDicsussions?.find(
              ({ comments }) =>
                comments.find(
                  (comment) => comment.commentGuid === annotation.Id,
                ),
            );

            if (
              commentForThisAnnotation &&
              data &&
              annotation.getCustomData('resolved') !== 'true'
            ) {
              AttachmentsAPI.deleteCommentThread(
                data.attachmentId,
                commentForThisAnnotation.commentThread.threadId,
              ).then(() => refetch());
            }
          });
          break;
        default:
      }
    },
    [beforeAddingAnnotation, data, filteredDicsussions, refetch],
  );

  const onAnnotationsSelect = useCallback(
    (
      _annotations: Core.Annotations.Annotation[],
      action: AnnotationSelectActions,
    ) => {
      if (action === 'deselected') {
        const selectedAnnotations =
          instance.current?.Core.annotationManager.getSelectedAnnotations();

        if (selectedAnnotations && selectedAnnotations.length > 0) {
          instance.current?.Core.annotationManager
            .getAnnotationsList()
            .forEach(decreaseAnnotationOpacity);
        } else {
          instance.current?.Core.annotationManager
            .getAnnotationsList()
            .forEach(increaseAnnotationOpacity);
        }
        setActiveAnnotation(null);
        setActiveDiscussion(null);
        setAddNewCommentFormVisiblity(false);

        return;
      }
      instance.current?.Core.annotationManager
        .getAnnotationsList()
        .forEach(decreaseAnnotationOpacity);
      _annotations.forEach(increaseAnnotationOpacity);
      if (_annotations.length === 1 && src && extname(src) === 'pdf') {
        instance.current?.Core.annotationManager.jumpToAnnotation(
          _annotations[0],
        );
      }
      setActiveAnnotation(_annotations[0]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      instance,
      setActiveAnnotation,
      setActiveDiscussion,
      setAddNewCommentFormVisiblity,
      src,
      data,
    ],
  );

  const onToolModeUpdated = useCallback(
    (newTool: Core.Tools.Tool) => {
      if (setActiveToolOverlay) {
        setActiveToolOverlay(newTool.name ?? '');
      }
      if (setActiveTool) {
        setActiveTool(newTool.name ?? '');
      }
      if (previewConfig?.isOverlay) {
        values.tool = newTool.name ?? '';
        const searchParamsList = stringify(values);

        history.replace({
          search: searchParamsList,
        });
      }
    },
    [history, previewConfig?.isOverlay, setActiveToolOverlay, values],
  );

  const onZoomUpdated = useCallback(
    (_zoom: number) => {
      setZoom(_zoom);
      setAddNewCommentFormVisiblity(false);
      setActiveDiscussion(null);
      if (previewConfig?.zoomTogether) {
        values.zoom = _zoom.toString();
        const searchParamsList = stringify(values);

        history.replace({
          search: searchParamsList,
        });
      }
    },
    [previewConfig?.zoomTogether, values, history],
  );

  const onRotationUpdated = useCallback(
    (_rotate: number) => {
      if (_rotate !== rotate) {
        setRotate(_rotate);
        setAddNewCommentFormVisiblity(false);
        setActiveDiscussion(null);
        if (previewConfig?.rotateTogether) {
          history.replace({
            search: `rotate=${_rotate.toString()}`,
          });
        }
      }
    },
    [
      rotate,
      setAddNewCommentFormVisiblity,
      setActiveDiscussion,
      previewConfig?.rotateTogether,
      history,
    ],
  );

  const attachEventListeners = useCallback(
    (_instance: WebViewerInstance) => {
      _instance.Core.documentViewer.addEventListener(
        'annotationsLoaded',
        onAnnotationsLoaded,
      );
      _instance.Core.annotationManager.addEventListener(
        'annotationChanged',
        onAnnotationsChange,
      );
      _instance.Core.annotationManager.addEventListener(
        'annotationSelected',
        onAnnotationsSelect,
      );
      _instance.Core.documentViewer.addEventListener(
        'toolModeUpdated',
        onToolModeUpdated,
      );
      _instance.Core.documentViewer.addEventListener(
        'zoomUpdated',
        onZoomUpdated,
      );
      _instance.Core.documentViewer.addEventListener(
        'rotationUpdated',
        onRotationUpdated,
      );
    },
    [
      onAnnotationsChange,
      onAnnotationsSelect,
      onAnnotationsLoaded,
      onToolModeUpdated,
      onZoomUpdated,
      onRotationUpdated,
    ],
  );

  const removeEventListeners = useCallback(
    (_instance: WebViewerInstance) => {
      _instance.Core.documentViewer.removeEventListener(
        'annotationsLoaded',
        onAnnotationsLoaded,
      );
      _instance.Core.annotationManager.removeEventListener(
        'annotationChanged',
        onAnnotationsChange,
      );
      _instance.Core.annotationManager.removeEventListener(
        'annotationSelected',
        onAnnotationsSelect,
      );
      _instance.Core.documentViewer.removeEventListener(
        'toolModeUpdated',
        onToolModeUpdated,
      );
      _instance.Core.documentViewer.removeEventListener(
        'zoomUpdated',
        onZoomUpdated,
      );
      _instance.Core.documentViewer.removeEventListener(
        'rotationUpdated',
        onRotationUpdated,
      );
    },
    [
      onAnnotationsChange,
      onAnnotationsSelect,
      onAnnotationsLoaded,
      onToolModeUpdated,
      onZoomUpdated,
      onRotationUpdated,
    ],
  );

  useEffect(() => {
    if (
      data &&
      ref.current &&
      (instance.current === null || instance.current === undefined) &&
      discussionsInAttachment
    ) {
      WebViewer(
        getWebviewerConfig({
          initialDoc:
            `${data.location}?phash=${data.attachmentId}` ||
            `${data.thumbLocation}?phash=${data.attachmentId}`,
          annotationUser: fullName(user),
        }),
        ref.current,
      )
        .then((_instance) => {
          instance.current = _instance;
          _instance.Core.PDFNet.initialize();
          reRender({});
          setupToolsForAnnotations(_instance);
          attachEventListeners(_instance);
          importAnnotations(_instance);
        })
        .catch(() => {});
    }
  });

  useEffect(() => {
    if (instance.current) {
      removeEventListeners(instance.current);
      attachEventListeners(instance.current);
    }

    const cleanup = addEventListenerToFindCoordinates();

    return () => {
      if (instance.current) {
        removeEventListeners(instance.current);
      }
      cleanup();
    };
  }, [
    onAnnotationsChange,
    onAnnotationsSelect,
    addEventListenerToFindCoordinates,
    removeEventListeners,
    attachEventListeners,
  ]);

  useEffect(() => {
    if (activeDiscussion && discussionsInAttachment) {
      const activeDiscussionInDiscussions =
        discussionsInAttachment.discussions.find(
          (e) =>
            e.commentThread.threadId ===
            activeDiscussion.commentThread.threadId,
        );

      if (activeDiscussionInDiscussions) {
        setActiveDiscussion(activeDiscussionInDiscussions);
      } else {
        setActiveDiscussion(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discussionsInAttachment]);

  useEffect(() => {
    if (setChatSectionVisiblity) {
      if (
        discussionsInAttachment &&
        filteredDicsussions.length === 0 &&
        instance.current?.Core.annotationManager.getAnnotationsList().length ===
          0
      ) {
        setChatSectionVisiblity(false);
      } else if (discussionsInAttachment && filteredDicsussions?.length > 0) {
        setChatSectionVisiblity(true);
      }
    }
    if (activeAnnotation) {
      const discussion = filteredDicsussions.find((e) =>
        e.comments.find((c) => c.commentGuid === activeAnnotation.Id),
      );

      if (discussion) {
        setActiveDiscussion(discussion);
      } else {
        setAddNewCommentFormVisiblity(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAnnotation, discussionsInAttachment, filteredDicsussions]);

  useEffect(() => {
    if (activeAnnotation && instance.current) {
      const discussion = getDiscussionFoAnnotation(activeAnnotation);

      if (!discussion) {
        const annotationsListInDescOrder =
          instance.current.Core.annotationManager.getAnnotationsList();
        const prevAddedAnnotation =
          annotationsListInDescOrder.length > 1
            ? annotationsListInDescOrder[annotationsListInDescOrder.length - 2]
            : null;

        if (prevAddedAnnotation) {
          const isDiscussionAvailableForPrevAnnotation =
            getDiscussionFoAnnotation(prevAddedAnnotation);

          if (!isDiscussionAvailableForPrevAnnotation) {
            instance.current.Core.annotationManager.deleteAnnotation(
              prevAddedAnnotation,
            );
          }
        }
      }
    }
  }, [activeAnnotation, getDiscussionFoAnnotation]);

  useEffect(() => {
    if (instance.current && data) {
      setShowResolvedComments(false);
      setFloatingFormCoordinates({ x: 0, y: 0 });
      instance.current.UI.loadDocument(
        `${data.location}?phash=${data.attachmentId}`,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, instance]);

  useEffect(() => {
    if (filteredDicsussions && instance.current) {
      const allComments = mergeAllComments(filteredDicsussions);

      instance.current.Core.documentViewer?.setDocumentXFDFRetriever(
        async () => allComments,
      );
    }
  }, [filteredDicsussions, instance]);

  useEffect(() => {
    if (
      resolvedDiscussions &&
      instance.current &&
      showResolvedComments !== undefined
    ) {
      const { annotationManager } = instance.current.Core;

      const annotationList = annotationManager.getAnnotationsList();
      const guidList = resolvedDiscussions
        .map((e) => e.comments)
        .flatMap((e) => e)
        .map((comment) => comment.commentGuid);

      const resolvedAnnotationList = annotationList.filter((ann) =>
        guidList.includes(ann.Id),
      );

      resolvedAnnotationList.forEach((ann) =>
        updateAnnotationVisiblity(!showResolvedComments, ann),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResolvedComments]);

  useEffect(() => {
    const zoomFromRouter = new URLSearchParams(params.search).get('zoom');

    if (
      zoomFromRouter !== null &&
      zoomFromRouter !== undefined &&
      zoom !== Number(zoomFromRouter) &&
      previewConfig?.zoomTogether
    ) {
      instance.current?.UI.setZoomLevel(Number(zoomFromRouter));
    }
  }, [zoom, params, instance, previewConfig?.zoomTogether]);

  useEffect(() => {
    const rotateFromRouter = new URLSearchParams(params.search).get('rotate');

    if (
      rotateFromRouter !== null &&
      rotateFromRouter !== undefined &&
      rotate !== Number(rotateFromRouter) &&
      previewConfig?.rotateTogether
    ) {
      instance.current?.Core.documentViewer.setRotation(
        Number(rotateFromRouter),
      );
    }
  }, [params, rotate, instance, previewConfig?.rotateTogether]);

  useEffect(() => {
    const toolFromRouter = new URLSearchParams(params.search).get('tool');
    const docViewer = instance.current?.Core.documentViewer;

    if (
      toolFromRouter !== null &&
      toolFromRouter !== undefined &&
      activeTool !== toolFromRouter &&
      docViewer &&
      previewConfig?.isOverlay
    ) {
      docViewer.setToolMode(docViewer.getTool(toolFromRouter));
    }
  }, [activeTool, previewConfig?.isOverlay, params, instance]);

  useEffect(() => {
    if (
      annotationVisiblity !== undefined &&
      instance.current &&
      previewConfig?.isOverlay
    ) {
      const { annotationManager } = instance.current.Core;
      const annotationsList = annotationManager.getAnnotationsList();
      const guidList = discussions
        .map((e) => e.comments)
        .flatMap((e) => e)
        .map((comment) => comment.commentGuid);

      const resolvedAnnotationList = annotationsList.filter((ann) =>
        guidList.includes(ann.Id),
      );

      if (annotationVisiblity) {
        resolvedAnnotationList.forEach((ann) =>
          updateAnnotationVisiblity(false, ann),
        );
      } else {
        resolvedAnnotationList.forEach((ann) =>
          updateAnnotationVisiblity(true, ann),
        );
      }
      annotationManager.deselectAllAnnotations();
    }
  }, [
    annotationVisiblity,
    discussions,
    previewConfig?.isOverlay,
    updateAnnotationVisiblity,
  ]);

  useEffect(() => {
    const scrollTopFromRouter = new URLSearchParams(params.search).get(
      'scrollTop',
    );

    const timeout = setTimeout(() => {
      if (
        scrollTopFromRouter !== null &&
        scrollTopFromRouter !== undefined &&
        scrollTop.current !== Number(scrollTopFromRouter) &&
        !isHover &&
        instance.current &&
        previewConfig?.scrollTogether
      ) {
        const documentContainer =
          instance?.current.UI.iframeWindow.document?.querySelector(
            '.DocumentContainer',
          );

        if (documentContainer) {
          scrollTop.current = Number(scrollTopFromRouter);
          documentContainer.scrollTop = Number(scrollTopFromRouter);
        }
      }
    }, 200);

    return () => clearTimeout(timeout);
  }, [
    activeTool,
    params,
    instance,
    previewConfig?.scrollTogether,
    scrollTop,
    isHover,
  ]);

  useEffect(() => {
    const scrollLeftFromRouter =
      new URLSearchParams(params.search).get('scrollLeft') ?? 0;

    const timeout = setTimeout(() => {
      if (
        scrollLeftFromRouter !== null &&
        scrollLeftFromRouter !== undefined &&
        scrollLeft.current !== Number(scrollLeftFromRouter) &&
        !isHover &&
        instance?.current &&
        previewConfig?.scrollTogether
      ) {
        const documentContainer =
          instance?.current.UI.iframeWindow.document?.querySelector(
            '.DocumentContainer',
          );

        if (documentContainer) {
          scrollLeft.current = Number(scrollLeftFromRouter);
          documentContainer.scrollLeft = Number(scrollLeftFromRouter);
        }
      }
    }, 200);

    return () => clearTimeout(timeout);
  }, [params, instance, previewConfig?.scrollTogether, scrollLeft, isHover]);

  const onPrivateButtonClick = () => {
    setPrivate((prevState) => !prevState);
  };

  return {
    instance,
    ref,
    addNewCommentFormVisiblity,
    floatingFormCoordinates,
    activeDiscussion,
    setAddNewCommentFormVisiblity,
    filteredDicsussions,
    activeAnnotation,
    onSubmit,
    isPrivate,
    onPrivateButtonClick,
    setActiveAnnotation,
    setActiveDiscussion,
    setFloatingFormCoordinates,
    activeTool,
    zoom,
    showResolvedComments,
    setShowResolvedComments,
    discussionsInAttachment,
    discussions,
    resolvedDiscussions,
  };
};
